/** Teams and their categories. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface ProjectCategory {
  id: string
  team: string
  name: string
  description: string
  position: number
  isActive: boolean
  projectCount: number
  rowVersion: number
}

export interface TeamOverview {
  team: string
  label: string
  /** For the team bar, where the full "Enterprise Data Enablement & Engineering" will not fit. */
  shortLabel: string
  projectCount: number
  categories: ProjectCategory[]
}

const key = (includeInactive: boolean) => ['teams', includeInactive] as const

export function useTeams(includeInactive = false) {
  return useQuery({
    queryKey: key(includeInactive),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/teams', {
        params: { query: { include_inactive: includeInactive } },
      })
      return data as unknown as { teams: TeamOverview[]; totalProjects: number }
    },
  })
}

export function useCreateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { team: string; name: string; description?: string }) => {
      const { data } = await api.POST('/api/v1/teams/{team}/categories', {
        params: { path: { team: input.team as never } },
        body: { name: input.name, description: input.description ?? '' } as never,
      })
      return data as unknown as ProjectCategory
    },
    // Both the bar and the management screen show counts, so refresh every variant.
    onSuccess: () => void client.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useUpdateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      name?: string
      description?: string
      position?: number
      isActive?: boolean
      rowVersion: number
    }) => {
      const { data } = await api.PATCH('/api/v1/categories/{category_id}', {
        params: { path: { category_id: input.id } },
        body: {
          name: input.name,
          description: input.description,
          position: input.position,
          is_active: input.isActive,
          row_version: input.rowVersion,
        } as never,
      })
      return data as unknown as ProjectCategory
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['teams'] })
      // A rename changes what every project row displays.
      void client.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
